import {
  IFarmInputProductDepotInfoInput,
  IDepot,
  IFarmInputProduct,
  IFarmInputProductDepotInfo,
} from 'constants/interfaces';

export const inventoryCsvHeaders: (keyof InventoryCsvRow)[] = [
  'Depot',
  'Product',
  'Cost Price',
  'Selling Price',
  'Min Order Quantity',
  'Min Free Delivery Quantity',
  'Stock On Hand',
];

export type InventoryCsvRow = {
  Depot: string;
  Product: string;
  'Cost Price'?: number;
  'Selling Price'?: number;
  'Min Order Quantity'?: number;
  'Min Free Delivery Quantity'?: number;
  'Stock On Hand'?: number;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
function isNumber(value?: string | number): boolean {
  return (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    !isNaN(Number(value.toString()))
  );
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function generateInventoryCsv(
  depots: IDepot[],
  products: IFarmInputProduct[],
  productDepotInfo: IFarmInputProductDepotInfo[],
): InventoryCsvRow[] {
  depots = depots.sort((a, b) => a.name.localeCompare(b.name));
  products = products.sort((a, b) => a.name.localeCompare(b.name));

  type ProductMap = {[productId: string]: IFarmInputProductDepotInfo};
  type DepotProductMap = {[depotId: string]: ProductMap};

  const depotProductMap: DepotProductMap = {};
  for (const info of productDepotInfo) {
    if (!depotProductMap[info.depotId]) depotProductMap[info.depotId] = {};
    depotProductMap[info.depotId][info.productId] = info;
  }

  const rows: InventoryCsvRow[] = [];
  for (const depot of depots) {
    for (const product of products) {
      const info = depotProductMap[depot.id]?.[product.id];
      rows.push({
        Depot: depot.name,
        Product: product.name,
        'Cost Price': info?.costPrice,
        'Selling Price': info?.price,
        'Min Order Quantity': info?.minOrderQuantity,
        'Min Free Delivery Quantity': info?.minOrderQuantityFreeDelivery,
        'Stock On Hand': info?.stockOnHand,
        // isActive: info?.isActive, // not visible on inventory form
      });
    }
  }
  return rows;
}

type InventoryCsvMapping = {
  input?: IFarmInputProductDepotInfoInput[];
  errors: InventoryCsvMappingErrors;
};

type InventoryCsvMappingErrors = string[];

type InventoryCsvFieldMapping<T> = {
  value?: T;
  errors: InventoryCsvMappingErrors;
};

export class InventoryCsvMapper {
  static NO_ROWS = () => 'CSV contains 0 rows.';
  static MISSING_COLUMN = (column: keyof InventoryCsvRow) =>
    `CSV does not have column '${column}'. Please use template file as reference.`;
  static UNKNOWN_DEPOT = (line: number, depotName: string) =>
    `Line ${line}: no depot exists with name '${depotName}'`;
  static UNKNOWN_PRODUCT = (line: number, productName: string) =>
    `Line ${line}: no product exists with name '${productName}'`;
  static INVALID_NUMBER = (
    line: number,
    column: keyof InventoryCsvRow,
    value: any,
  ) => `Line ${line}: ${column} is not a valid number '${value}'`;

  private readonly depotNameMap: {[depotName: string]: IDepot};
  private readonly productNameMap: {[productName: string]: IFarmInputProduct};

  constructor(depots: IDepot[], products: IFarmInputProduct[]) {
    this.depotNameMap = {};
    this.productNameMap = {};
    depots.forEach((depot) => (this.depotNameMap[depot.name] = depot));
    products.forEach(
      (product) => (this.productNameMap[product.name] = product),
    );
  }

  map(rows: InventoryCsvRow[]): InventoryCsvMapping {
    const noRows = this.assertRowCount(rows);
    if (noRows) return {input: undefined, errors: noRows};

    const missingColumns = this.assertNoMissingColumns(rows);
    if (missingColumns) return {input: undefined, errors: missingColumns};

    const input: IFarmInputProductDepotInfoInput[] = [];
    const errors: InventoryCsvMappingErrors = [];
    for (let i = 0; i < rows.length; i++) {
      const mappedRow = this.mapRow(i + 1, rows[i]);
      if (mappedRow.input) input.push(...mappedRow.input);
      errors.push(...mappedRow.errors);
    }

    if (errors.length) return {input: undefined, errors};
    return {input, errors};
  }

  private assertRowCount(rows: any[]): InventoryCsvMappingErrors | undefined {
    if (rows.length === 0) return [InventoryCsvMapper.NO_ROWS()];
  }

  private assertNoMissingColumns(
    rows: any[],
  ): InventoryCsvMappingErrors | undefined {
    const errors: string[] = [];
    const firstRow = rows[0];
    for (const header of inventoryCsvHeaders) {
      // empty cells are empty strings when uploaded not undefined
      if (firstRow[header] === undefined)
        errors.push(InventoryCsvMapper.MISSING_COLUMN(header));
    }
    if (errors.length) return errors;
  }

  private mapRow(lineNum: number, row: InventoryCsvRow): InventoryCsvMapping {
    const errors: string[] = [];

    const depot = this.depotNameMap[row.Depot];
    if (!depot)
      errors.push(InventoryCsvMapper.UNKNOWN_DEPOT(lineNum, row.Depot));

    const product = this.productNameMap[row.Product];
    if (!product)
      errors.push(InventoryCsvMapper.UNKNOWN_PRODUCT(lineNum, row.Product));

    const mapFloatCell = (key: keyof InventoryCsvRow) =>
      this.mapFloatCell(lineNum, row, key);

    const costPrice = mapFloatCell('Cost Price');
    errors.push(...costPrice.errors);
    const sellingPrice = mapFloatCell('Selling Price');
    errors.push(...sellingPrice.errors);
    const minOrderQty = mapFloatCell('Min Order Quantity');
    errors.push(...minOrderQty.errors);
    const minFreeDeliveryQty = mapFloatCell('Min Free Delivery Quantity');
    errors.push(...minFreeDeliveryQty.errors);
    const stock = mapFloatCell('Stock On Hand');
    errors.push(...stock.errors);

    if (errors.length) return {input: undefined, errors};

    // const isEmptyRow =
    //   costPrice.value === undefined &&
    //   sellingPrice.value === undefined &&
    //   minOrderQty.value === undefined &&
    //   minFreeDeliveryQty.value === undefined &&
    //   stock.value === undefined;
    // if (isEmptyRow) return {input: undefined, errors: []};

    return {
      input: [
        {
          depotId: depot.id.toString(),
          productId: product.id.toString(),
          costPrice: costPrice.value,
          price: sellingPrice.value,
          minOrderQuantity: minOrderQty.value,
          minOrderQuantityFreeDelivery: minFreeDeliveryQty.value,
          stockOnHand: stock.value,
          isActive: true,
        },
      ],
      errors: [],
    };
  }

  private mapFloatCell(
    lineNum: number,
    row: InventoryCsvRow,
    key: keyof InventoryCsvRow,
  ): InventoryCsvFieldMapping<number> {
    const value = row[key];

    if (value === '') return {value: undefined, errors: []};

    if (value && !isNumber(value)) {
      return {
        value: undefined,
        errors: [InventoryCsvMapper.INVALID_NUMBER(lineNum, key, value)],
      };
    }

    if (value && isNumber(value))
      return {value: parseFloat(value as any), errors: []};

    console.error('Unhandled case', {lineNum, row, key});
    throw new Error('Failed to map csv. Please try again later');
  }
}
